import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';
import { ReceiveApi } from '../../utils/Apis';
import { fireApi } from '../../utils/FireApi';
import { fIndianAmount, readIndianAmountInput } from '../../utils/formatNumber';

export default function ReceivableDialogRedux({
  openPayment,
  setOpenPayment,
  userId,
  invoiceId,
  tdsRate = 4,
  tdsOnGstApplicable = false,
  onSuccess
}) {
  const dispatch = useDispatch();

  const PaymentMode = ['RTGS', 'NEFT', 'BANK TRANSFER'];

  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      receivedOn: '',
      paymentMode: '',
      netAmountReceived: '',
      gstCharged: '',
      tdsDeduction: '',
      netAmountForCommission: '',
      bankAccountId: '',
      accountNumber: '',
      ifscCode: '',
      utrNo: ''
    }
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!openPayment) {
      reset();
    }
  }, [openPayment, reset]);

  // Fetch bank accounts when dialog opens
  useEffect(() => {
    let isMounted = true;
    const fetchBankAccounts = async () => {
      if (!openPayment) return;
      setLoadingBanks(true);
      setBankError(null);
      try {
        const res = await fireApi({
          endpoint: ReceiveApi?.bankAccounts || 'receivable/v1/bank-accounts',
          method: 'get'
        });
        if (isMounted) {
          if (res?.success && res?.data) {
            const list = Array.isArray(res.data?.data)
              ? res.data.data
              : Array.isArray(res.data)
              ? res.data
              : [];
            setBankAccounts(list);
          } else {
            setBankError(res?.message || 'Failed to fetch bank accounts');
          }
        }
      } catch (err) {
        if (isMounted) {
          setBankError(err?.message || 'Error fetching bank accounts');
        }
      } finally {
        if (isMounted) {
          setLoadingBanks(false);
        }
      }
    };

    fetchBankAccounts();
    return () => {
      isMounted = false;
    };
  }, [openPayment]);

  // Watch Net Amount for Commission and calculate GST, TDS, Net Received
  const commissionVal = watch('netAmountForCommission');

  useEffect(() => {
    if (
      commissionVal !== undefined &&
      commissionVal !== null &&
      commissionVal !== '' &&
      !Number.isNaN(Number(commissionVal))
    ) {
      const commission = parseFloat(commissionVal);
      // GST Charged = Net Amount for Commission * 18 / 100
      const gst = parseFloat(((commission * 18) / 100).toFixed(2));
      // TDS Deduction = Net Amount for Commission * Selected TDS Rate / 100
      const rate = Number(tdsRate) || 0;
      const tds = parseFloat(((commission * rate) / 100).toFixed(2));
      // Net Amount Received = Net Amount for Commission + GST Charged - TDS Deduction
      const netReceived = parseFloat((commission + gst - tds).toFixed(2));

      setValue('gstCharged', gst, { shouldValidate: true });
      setValue('tdsDeduction', tds, { shouldValidate: true });
      setValue('netAmountReceived', netReceived, { shouldValidate: true });
    } else {
      setValue('gstCharged', '', { shouldValidate: false });
      setValue('tdsDeduction', '', { shouldValidate: false });
      setValue('netAmountReceived', '', { shouldValidate: false });
    }
  }, [commissionVal, tdsRate, setValue]);

  // Watch selected bank and auto-populate Account Number and IFSC Code
  const selectedBankId = watch('bankAccountId');

  useEffect(() => {
    if (selectedBankId) {
      const selected = bankAccounts.find((b) => String(b.bankAccountId) === String(selectedBankId));
      if (selected?.accountNumber) {
        setValue('accountNumber', selected.accountNumber, { shouldValidate: true });
      } else {
        setValue('accountNumber', '');
      }
      if (selected?.ifscCode) {
        setValue('ifscCode', selected.ifscCode, { shouldValidate: true });
      } else {
        setValue('ifscCode', '');
      }
    } else {
      setValue('accountNumber', '');
      setValue('ifscCode', '');
    }
  }, [selectedBankId, bankAccounts, setValue]);

  const onSubmit = async (data) => {
    const selectedBank = bankAccounts.find(
      (b) => String(b.bankAccountId) === String(data.bankAccountId)
    );

    const payload = {
      userId,
      invoiceId,
      receivedOn: data.receivedOn,
      paymentMode: data.paymentMode,
      netAmountReceived: parseFloat(data.netAmountReceived),
      gstCharged: parseFloat(data.gstCharged),
      tdsDeduction: parseFloat(data.tdsDeduction),
      netAmountForCommission: parseFloat(data.netAmountForCommission),
      tdsRate: Number(tdsRate),
      tdsOnGstApplicable,
      ...(data.utrNo?.trim() ? { utrNo: data.utrNo.trim() } : {}),
      ...(data.bankAccountId ? { bankAccountId: Number(data.bankAccountId) } : {}),
      ...(data.accountNumber ? { accountNumber: data.accountNumber } : {}),
      ...(data.ifscCode ? { ifscCode: data.ifscCode } : {}),
      ...(selectedBank?.bankName ? { bankName: selectedBank.bankName } : {})
    };

    if (
      Number.isNaN(payload.netAmountReceived) ||
      Number.isNaN(payload.gstCharged) ||
      Number.isNaN(payload.tdsDeduction) ||
      Number.isNaN(payload.netAmountForCommission)
    ) {
      dispatch(setApiError('Please enter valid numeric values for all amount fields.'));
      return;
    }

    if (tdsOnGstApplicable && !(payload.tdsDeduction > 0)) {
      dispatch(setApiError('TDS deduction is required when TDS is applicable on GST.'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await dispatch(saveApiData(ReceiveApi?.invoicePaidApi, payload));
      if (result) {
        dispatch(setApiSuccess('Payment submitted successfully'));
        reset();
        setOpenPayment(false);
        if (typeof onSuccess === 'function') onSuccess(result);
      }
    } catch (err) {
      console.error('Submit payment failed:', err);
      dispatch(setApiError(err?.message || 'Payment submission failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={openPayment}
      onClose={() => setOpenPayment(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            px: 3,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
            Payment Invoice
          </Typography>
          <IconButton size="small" onClick={() => setOpenPayment(false)} sx={{ color: '#64748b' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            {/* Received On */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                Received On <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Controller
                name="receivedOn"
                control={control}
                rules={{ required: 'Received On is required' }}
                render={({ field }) => (
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      const formattedValue =
                        newValue && dayjs(newValue).isValid()
                          ? dayjs(newValue).format('YYYY-MM-DD')
                          : '';
                      field.onChange(formattedValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        error={!!errors.receivedOn}
                        helperText={errors.receivedOn?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: 'DD/MM/YYYY'
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Payment Mode */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                Payment Mode <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Controller
                name="paymentMode"
                control={control}
                rules={{ required: 'Payment mode is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    size="small"
                    displayEmpty
                    sx={{ borderRadius: '8px' }}
                    renderValue={(selected) =>
                      selected ? selected : <span style={{ color: '#94a3b8' }}>Select option</span>
                    }
                  >
                    {PaymentMode.map((mode) => (
                      <MenuItem key={mode} value={mode}>
                        {mode}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.paymentMode && (
                <Typography variant="caption" color="error">
                  {errors.paymentMode.message}
                </Typography>
              )}
            </Grid>

            {/* Net Amount for Commission */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                Net Amount for Commission <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Controller
                name="netAmountForCommission"
                control={control}
                rules={{
                  required: 'Net Amount for Commission is required',
                  validate: (v) => (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={fIndianAmount(field.value) ?? ''}
                    onChange={(e) => {
                      const rawValue = readIndianAmountInput(e);
                      if (rawValue !== null) field.onChange(rawValue);
                    }}
                    fullWidth
                    size="small"
                    type="text"
                    inputProps={{ inputMode: 'decimal' }}
                    placeholder="Enter commission amount"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    error={!!errors.netAmountForCommission}
                    helperText={errors.netAmountForCommission?.message}
                  />
                )}
              />
            </Grid>

            {/* GST Charged (18%) */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                GST Charged (18%) <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Controller
                name="gstCharged"
                control={control}
                rules={{
                  required: 'GST Charged is required',
                  validate: (v) => (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={fIndianAmount(field.value) ?? ''}
                    fullWidth
                    size="small"
                    type="text"
                    placeholder="Auto calculated"
                    InputProps={{
                      readOnly: true
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc'
                      }
                    }}
                    error={!!errors.gstCharged}
                    helperText={errors.gstCharged?.message}
                  />
                )}
              />
            </Grid>

            {/* TDS Deduction */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                TDS Deduction ({tdsRate}%) <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Controller
                name="tdsDeduction"
                control={control}
                rules={{
                  required: 'TDS Deduction is required',
                  validate: (v) => (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={fIndianAmount(field.value) ?? ''}
                    fullWidth
                    size="small"
                    type="text"
                    placeholder="Auto calculated"
                    InputProps={{
                      readOnly: true
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc'
                      }
                    }}
                    error={!!errors.tdsDeduction}
                    helperText={errors.tdsDeduction?.message}
                  />
                )}
              />
            </Grid>

            {/* Net Amount Received */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                Net Amount Received <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Controller
                name="netAmountReceived"
                control={control}
                rules={{
                  required: 'Net Amount Received is required',
                  validate: (v) => (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={fIndianAmount(field.value) ?? ''}
                    fullWidth
                    size="small"
                    type="text"
                    placeholder="Auto calculated"
                    InputProps={{
                      readOnly: true
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc'
                      }
                    }}
                    error={!!errors.netAmountReceived}
                    helperText={errors.netAmountReceived?.message}
                  />
                )}
              />
            </Grid>

            {/* Select Bank Name */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                Select Bank Name <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <Controller
                name="bankAccountId"
                control={control}
                rules={{ required: 'Bank Name is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    size="small"
                    displayEmpty
                    disabled={loadingBanks}
                    sx={{ borderRadius: '8px' }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: '#94a3b8' }}>
                            {loadingBanks ? 'Loading banks...' : 'Select Bank'}
                          </span>
                        );
                      }
                      const b = bankAccounts.find(
                        (item) => String(item.bankAccountId) === String(selected)
                      );
                      return b ? b.bankName : selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>-- Select Bank --</em>
                    </MenuItem>
                    {bankAccounts.map((bank) => (
                      <MenuItem key={bank.bankAccountId} value={bank.bankAccountId}>
                        {bank.bankName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.bankAccountId && (
                <Typography variant="caption" color="error">
                  {errors.bankAccountId.message}
                </Typography>
              )}
              {bankError && (
                <Typography variant="caption" color="error">
                  {bankError}
                </Typography>
              )}
            </Grid>

            {/* Account Number */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                Account Number
              </Typography>
              <Controller
                name="accountNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder="Auto populated on bank selection"
                    InputProps={{
                      readOnly: true
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc'
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* IFSC Code */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                IFSC Code
              </Typography>
              <Controller
                name="ifscCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder="Auto populated on bank selection"
                    InputProps={{
                      readOnly: true
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc'
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* UTR No */}
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                UTR No
              </Typography>
              <Controller
                name="utrNo"
                control={control}
                rules={{
                  maxLength: { value: 100, message: 'UTR number cannot exceed 100 characters' },
                  pattern: {
                    value: /^[A-Za-z0-9 /\\._:#-]*$/,
                    message: 'UTR number can have only letters, digits, space and / \\ . _ : # -'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder="Enter UTR number"
                    inputProps={{ maxLength: 100 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    error={!!errors.utrNo}
                    helperText={errors.utrNo?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setOpenPayment(false)}
            variant="outlined"
            disabled={submitting}
            sx={{ borderRadius: '8px', color: '#64748b', borderColor: '#cbd5e1' }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              borderRadius: '8px',
              bgcolor: '#13519d',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              '&:hover': { bgcolor: '#0d3d75' }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
